import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CreditLogType, CreditPackType, PaymentMethodsEnum, Order, Customer, Invoice, User } from '@app/models';
import { AppService, ErrorHandlerService, NotyService, PaymentService } from '@app/services';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { LoaderService } from '@app/shared';
import { CustomCreditPackModalService } from './custom-credit-pack-modal/custom-credit-pack-modal.service';
import { EditPaymentInfoModalService } from '@app/pages/configurations/configurations-plan/edit-payment-info-modal/edit-payment-info-modal.service';

enum CreditPlanName {
  P_10 = 'P_10',
  P_50 = 'P_50',
  P_100 = 'P_100',
  P_500 = 'P_500',
  P_1000 = 'P_1000',
  P_CUSTOM = 'P_CUSTOM'
}

@Component({
  selector: 'app-additional-validation',
  templateUrl: './additional-validation.component.html',
  styleUrls: ['./additional-validation.component.scss'],
  standalone: false
})
export class AdditionalValidationComponent implements OnInit {
  @Input() currentCustomer: Customer;
  @Input() currentOrder: Order;
  @Input() currency = 'BRL';
  @Input() currencyCode: string;
  @Input() currentUser: User;
  @Input() isMobile: boolean;
  @Output() currentCustomerChange = new EventEmitter<Customer>();
  @Output() invoice = new EventEmitter<Invoice>();
  isLoading = false;

  credits: CreditPackType[];
  creditLogs: CreditLogType[];
  creditLogPageNumber: number;
  creditLogIsLastPage = false;

  constructor(
    public translateService: TranslateService,
    public appService: AppService,
    private errorHandlerService: ErrorHandlerService,
    private paymentService: PaymentService,
    private customCreditPackModalService: CustomCreditPackModalService,
    private loaderService: LoaderService,
    private notyService: NotyService,
    private editPaymentInfoModalService: EditPaymentInfoModalService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.loaderService.show();

    this.paymentService
      .credits()
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: credits => {
          this.credits = credits;
        },
        error: error => this.errorHandlerService.handle(error)
      });

    setTimeout(() => {
      this.loadCreditLogs();
    }, 100);
  }

  loadCreditLogs(options: { page?: number } = {}) {
    const limit = 15;
    this.creditLogPageNumber = options.page || 1;
    this.paymentService.creditLogs({ limit, page: this.creditLogPageNumber }).subscribe({
      next: page => {
        this.creditLogs = (this.creditLogPageNumber === 1 ? [] : this.creditLogs || []).concat(page);
        this.creditLogIsLastPage = page.length < limit;
      },
      error: error => this.errorHandlerService.handle(error)
    });
  }

  buySelectedCredit(credit: CreditPackType) {
    this.editPaymentInfoModalService
      .open({
        currentCustomer: this.currentCustomer,
        currentOrder: this.currentOrder,
        creditsPack: credit
      })
      .subscribe({
        next: data => {
          this.currentCustomerChange.emit(data.currentCustomer);
          this.buyCredits(credit, data.payableWith, data.cardToken, credit.quantity);
        }
      });
  }

  openCustomCreditPackModal() {
    this.customCreditPackModalService.open({ currencyCode: this.currencyCode }).subscribe({
      next: creditPack => {
        if (creditPack) {
          this.buySelectedCredit(creditPack);
        }
      }
    });
  }

  buyCredits(credit: CreditPackType, paymentMethod?: PaymentMethodsEnum, cardToken?: string, quantity?: number) {
    if (!this.isLoading) {
      this.isLoading = true;
      this.loaderService.show();
      this.paymentService
        .buyCredits({
          package: credit.name,
          ...(paymentMethod ? { payment_method: paymentMethod } : {}),
          ...(cardToken ? { card_token: cardToken } : {}),
          ...(quantity && credit.name === CreditPlanName.P_CUSTOM ? { quantity } : {})
        })
        .pipe(
          finalize(() => {
            this.isLoading = false;
            this.loaderService.hide();
          })
        )
        .subscribe({
          next: invoice => {
            // this.invoices.unshift(invoice);
            this.invoice.emit(invoice);
            this.notyService.success(this.translateService.instant('notyService.orderRegistered'));
          },
          error: error => {
            this.errorHandlerService.handle(error);
          }
        });
    }
  }
}
